import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useMe} from '@/features/authentication/queries/useMe'
import {GuestTypeV3, HostTypeV3} from '@/features/authentication/types'
import useWindowDimensions from '@/hooks/useWindowDimensions'
import {USER_ROLE_GUEST} from '@/utilities/constants/user'
import {FC, Fragment, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {useGetMuralOfHost} from '../../queries/useGetMuralOfHost'
import {MURAL_TYPES} from '../../services/completeProfile.schema'
import {ToolTipActionsModal} from '../host-dashboard/actions-section/components/toolTipActionsModal/ToolTipActionsModal'
import HostProfileTaxiSection from '../host-profile-taxi-section/HostProfileTaxiSection'
import {ContainerRow} from '../host-profile/style'
import HostProfileMuralItem from './components/host-profile-mural-item/HostProfileMuralItem'
import {
    StyledFilterTab,
    StyledFilterTabs,
    StyledHostProfileMuralList,
    StyledHostProfileMuralListHeader,
    StyledHostProfileMuralListHeaderTitle,
    StyledInfoIcon
} from './style'
import BigInfoIcon from '@assets/images/BigInfoCircleIcon.png'
import {useGetFeaturedCreators} from '@/features/authentication/queries/useGetFeaturedCreators'

type HostProfileMuralSectionProps = {
    host: Partial<HostTypeV3>
}
const FILTERS_MAP = [undefined, MURAL_TYPES.MEDIA_PACKAGES]

const HostProfileMuralSection: FC<HostProfileMuralSectionProps> = ({host}) => {
    const {data: user} = useMe()
    const isGuest = user?.type === USER_ROLE_GUEST
    const {t} = useTranslation()
    const {breakpoints} = useTheme()
    const {width} = useWindowDimensions()
    const [isMobile, setIsMobile] = useState(width <= breakpoints.width.m)
    const limit = 10
    const [selectedTab, setSelectedTab] = useState(0)
    const {data, isLoading, isFetchingNextPage, fetchNextPage, isEmptyList} = useGetMuralOfHost({
        hostID: isGuest ? String(host.id) : undefined,
        limit,
        muralType: FILTERS_MAP[selectedTab]
    })
    const {data: featuredRookies} = useGetFeaturedCreators({})
    const guest = isGuest ? (user as GuestTypeV3) : null
    const residualElements =
        (data?.pages?.at(-1)?.meta.total as number) - (data?.pages?.at(-1)?.meta.page as number) * limit
    const muralList = data && data?.pages.flatMap(page => page.data)
    const [openToolTipModal, setOpenToolTipModal] = useState(false)
    const mediaPackagesCount = host.typeAttributes?.mediaPackagesCount
    const defaultItem = muralList?.find(
        item => item.url && item.muralType === (MURAL_TYPES.PHOTOS || MURAL_TYPES.VIDEOS)
    )

    const tabs = [
        mediaPackagesCount
            ? {id: 2, title: t('rookie_profile_page:packages_tab', {COUNTER: mediaPackagesCount})}
            : undefined
    ].filter((tab): tab is {id: number; title: string} => Boolean(tab))

    useEffect(() => {
        setIsMobile(width <= breakpoints.width.m)
    }, [width])
    const wallInserts = [
        {
            index: 1,
            content: featuredRookies?.length ? <HostProfileTaxiSection featuredRookie={featuredRookies} /> : null
        }
    ]
    if (isEmptyList && !isLoading) return null

    return (
        <ContainerRow>
            {isLoading && <Spinner />}
            <StyledHostProfileMuralList>
                <StyledHostProfileMuralListHeader>
                    <StyledHostProfileMuralListHeaderTitle>
                        {t('rookie_profile_page:mural')}
                    </StyledHostProfileMuralListHeaderTitle>
                    <StyledInfoIcon
                        fill="#00063D80"
                        width={isMobile ? 24 : 30}
                        height={isMobile ? 24 : 30}
                        onClick={() => {
                            setOpenToolTipModal(true)
                        }}
                    />
                </StyledHostProfileMuralListHeader>
                {!!tabs.length && (
                    <StyledFilterTabs>
                        {tabs.map(tab => (
                            <StyledFilterTab
                                key={tab.id}
                                isSelected={selectedTab === tab.id}
                                onClick={() => {
                                    setSelectedTab(tab.id)
                                }}
                            >
                                {tab.title}
                            </StyledFilterTab>
                        ))}
                    </StyledFilterTabs>
                )}

                {muralList?.length &&
                    muralList.map((item, idx) => {
                        const extraContent = wallInserts.find(item => item.index === idx)
                        return (
                            <Fragment key={item.id}>
                                <HostProfileMuralItem
                                    post={item}
                                    host={host}
                                    guestsBalance={isGuest ? guest?.typeAttributes.microMorgiBalance : undefined}
                                    defaultItem={
                                        defaultItem
                                            ? {url: defaultItem.url as string, muralType: defaultItem.muralType}
                                            : undefined
                                    }
                                />
                                {extraContent?.content}
                            </Fragment>
                        )
                    })}
                {residualElements > 0 &&
                    (isFetchingNextPage ? (
                        <Spinner center={false} inline={true} overlay={false} withMargin={false} />
                    ) : (
                        <Button
                            onClick={() => {
                                fetchNextPage()
                            }}
                        >
                            {t('rookie_profile_page:see_more_content', {
                                COUNTER: residualElements < limit ? residualElements : limit
                            })}
                        </Button>
                    ))}
            </StyledHostProfileMuralList>
            {openToolTipModal && (
                <ToolTipActionsModal
                    content={{
                        title: t('rookie_profile_preview:host_surprises:what_get_title'),
                        content: t('rookie_profile_preview:host_surprises:what_get_text'),
                        icon: () => <img src={BigInfoIcon} />
                    }}
                    onClose={() => {
                        setOpenToolTipModal(false)
                    }}
                />
            )}
        </ContainerRow>
    )
}
export default HostProfileMuralSection
